import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useState } from 'react';

import { css as emotionCss } from '@emotion/css';
import { Box, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { Calendar as BigCalendar, type View, Views, dayjsLocalizer } from 'react-big-calendar';

import Skeleton from '../../components/shared/Skeleton/Skeleton';
import { getEventFormatColor } from '../../constants/eventFormatColor';
import type { Event } from '../../Data/types';
import type { Gec_Events } from '../../gql/graphql';
import { useFilteredEvents } from '../../hooks/useFilteredEvents/useFilteredEvents';
import useParseEvents from '../../hooks/useParseEvents/useParseEvents';
import { selectedEventAtom, formModeAtom, showEventPaneAtom } from '../../state/atoms/atoms';

const localizer = dayjsLocalizer(dayjs);

export default function Calendar() {
  const [showEventPane, setshowEventPane] = useAtom(showEventPaneAtom);
  const [, setSelectedEvent] = useAtom(selectedEventAtom);
  const [, setFormMode] = useAtom(formModeAtom);
  const [currentDay, setCurrentDay] = useState<Date>(new Date());
  const [view, setView] = useState<View>(Views.MONTH);
  const filteredEvents = useFilteredEvents();
  const events: Event[] | undefined = useParseEvents((filteredEvents.data?.gec_events as Gec_Events[]) ?? undefined);
  const theme = useTheme();

  const styles = {
    calendar: {
      width: '100%',
      height: '100%',
      '& .rbc-toolbar': {
        margin: '10px',
      },
      '& .rbc-toolbar button': {
        fontWeight: 'bold',
      },
      '& .rbc-btn-group button': {
        color: 'inherit',
        fontFamily: 'inherit',
      },
      '& .rbc-active': {
        color: '#373a3c !important',
      },
      '& .rbc-now button': {
        color: theme.palette.mode === 'dark' ? theme.palette.secondary.contrastText : theme.palette.text.primary,
      },
      '& .rbc-off-range button': {
        color: theme.palette.mode === 'dark' ? theme.palette.secondary.contrastText : theme.palette.text.disabled,
      },
    },
  };
  const calendarClass = emotionCss(styles.calendar);

  return filteredEvents.loading ? (
    <Skeleton type="calendar" />
  ) : (
    <Box
      sx={{
        display: 'flex',
        border: `1px solid ${theme.palette.divider}`,
        height: '100%',
      }}
    >
      <BigCalendar
        className={calendarClass}
        localizer={localizer}
        events={events}
        startAccessor={(event) => {
          return event.eventDateTime!.start!.toDate();
        }}
        endAccessor={(event) => {
          return event.eventDateTime!.end!.toDate();
        }}
        view={view}
        date={currentDay}
        onNavigate={(date: Date) => setCurrentDay(date)}
        onView={(newView) => setView(newView)}
        onSelectEvent={(event) => {
          setFormMode('preview');
          setshowEventPane(!showEventPane);
          setSelectedEvent(event);
        }}
        eventPropGetter={(event) => {
          const backgroundColor = getEventFormatColor(event.eventFormat?.name);
          return {
            style: {
              backgroundColor,
              color: '#000000',
            },
          };
        }}
      />
    </Box>
  );
}
